import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocFromServer,
  serverTimestamp,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getApp } from 'firebase/app';
import { db } from '../firebase';
import { CartContext } from '../contexts/CartContext';
import { AuthContext } from '../contexts/AuthContext';
import { StoreContext } from '../contexts/StoreContext';
import stripePromise, {
  stripeConfigurationError,
  stripeConfiguredMode,
} from '../contexts/StripeContext';
import { formatPrice } from '../utils/priceFormatter';
import { useDisplayPreferences } from '../contexts/DisplayPreferencesContext';
import { MAX_CONTENT_WIDTH } from '../constants/layout';
import ContactInfoModal from '../components/ContactInfoModal';
import {
  adoptServerCheckoutAttempt,
  bindCheckoutAttemptQuote,
  buildCheckoutQuoteV2Request,
  checkoutErrorMessage,
  clearSubmittedQuote,
  createCheckoutQuoteV2,
  createPaymentIntentV2,
  discardCheckoutAttempt,
  getCheckoutAttempt,
  getOrCreateCheckoutAttempt,
  getResumableQuote,
  getSubmittedQuote,
  markQuoteSubmitted,
  parseCheckoutQuoteV2Response,
  paymentIntentReturnDisposition,
  submitFreeOrderV2,
} from '../services/paymentV2';
import {
  PAYMENT_V2_STORE_ID,
  paymentV2StoreGateAllows,
  readAuthoritativePaymentV2Store,
} from '../utils/paymentV2StoreGate';

function logCheckoutError({ quoteId, userID, step, errorMessage, errorCode }) {
  addDoc(collection(db, 'checkout_errors'), {
    timestamp: serverTimestamp(),
    sessionId: quoteId || '',
    quoteId: quoteId || '',
    userID: userID || '',
    platform: 'web',
    step,
    errorMessage: errorMessage || '',
    ...(errorCode != null ? { errorCode } : {}),
  }).catch(() => {});
}

function CheckoutForm({ quote, clientSecret }) {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useContext(AuthContext);
  const { markCheckoutSubmitted } = useContext(CartContext);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const returnUrl = `${window.location.origin}/order-confirmation?quoteId=${encodeURIComponent(quote.quoteId)}`;

  const handleConfirmationFailure = async (error) => {
    logCheckoutError({
      quoteId: quote.quoteId,
      userID: user?.uid,
      step: 'payment_confirmation',
      errorMessage: error?.message || 'Payment confirmation failed',
      errorCode: error?.code,
    });

    try {
      const result = await stripe.retrievePaymentIntent(clientSecret);
      if (result.error || !result.paymentIntent) {
        throw result.error || new Error('Stripe did not return a PaymentIntent.');
      }
      const disposition = paymentIntentReturnDisposition(result.paymentIntent.status);
      if (disposition === 'retryable') {
        clearSubmittedQuote(quote.quoteId);
        setErrorMessage(error?.message || 'This payment method was not accepted. Please try another method.');
        setIsProcessing(false);
        return;
      }
      if (disposition === 'restart') {
        discardCheckoutAttempt(quote.quoteId);
        window.location.assign('/cart');
        return;
      }
      if (disposition === 'pending') {
        setErrorMessage('Payment authentication is not complete. Continue with this same payment to avoid a duplicate.');
        setIsProcessing(false);
        return;
      }
    } catch (statusError) {
      logCheckoutError({
        quoteId: quote.quoteId,
        userID: user?.uid,
        step: 'payment_confirmation_status_unknown',
        errorMessage: statusError?.message || 'Payment status could not be retrieved',
        errorCode: statusError?.code,
      });
    }

    // An unknown or submitted result must never reopen checkout. The order
    // page will poll the deterministic quote ID and present a pending state.
    window.location.assign(returnUrl);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements || isProcessing) return;

    setIsProcessing(true);
    setErrorMessage(null);

    // Re-read the public, server-owned gate immediately before submission.
    // The live listener controls presentation; this point read closes the
    // stale-render window before local state becomes durably submitted.
    try {
      const authoritativeStore = await readAuthoritativePaymentV2Store(
        () => getDocFromServer(doc(db, 'stores', PAYMENT_V2_STORE_ID)),
      );
      if (!authoritativeStore) {
        setErrorMessage('Checkout is currently unavailable. No payment was submitted.');
        setIsProcessing(false);
        return;
      }
    } catch {
      setErrorMessage(
        'The checkout safety gate could not be verified. No payment was submitted.',
      );
      setIsProcessing(false);
      return;
    }

    try {
      const submission = await markCheckoutSubmitted({
        userId: user?.uid,
        quoteId: quote.quoteId,
        purchasedItems: quote.items,
      });
      if (submission.state === 'consumed') {
        window.location.assign(returnUrl);
        return;
      }
    } catch {
      setErrorMessage(
        'Secure cart recovery could not be saved. Payment was not submitted; enable persistent browser storage and try again.',
      );
      setIsProcessing(false);
      return;
    }

    if (!markQuoteSubmitted(quote.quoteId)) {
      setErrorMessage(
        'Secure checkout recovery could not be saved. Payment was not submitted; enable persistent browser storage and try again.',
      );
      setIsProcessing(false);
      return;
    }

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
        },
      });

      if (error) {
        await handleConfirmationFailure(error);
      }
    } catch (error) {
      await handleConfirmationFailure(error);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {errorMessage && (
        <div style={{ color: '#df1b41', marginTop: 10, fontSize: 14 }}>
          {errorMessage}
        </div>
      )}
      <button
        type="submit"
        disabled={!stripe || isProcessing}
        style={{
          marginTop: 16,
          width: '100%',
          padding: '10px 0',
          backgroundColor: isProcessing ? '#999' : 'var(--accent-color, #000)',
          color: '#fff',
          border: 'none',
          borderRadius: 6,
          fontSize: 16,
          fontWeight: 600,
          cursor: isProcessing ? 'not-allowed' : 'pointer',
        }}
      >
        {isProcessing ? 'Processing...' : `Pay ${formatPrice(quote.totalMinor, quote.currency)}`}
      </button>
    </form>
  );
}

export default function CheckoutView() {
  const {
    items,
    cartSubtotal,
    cartTax,
    estimatedTotal,
    cartPricingValid,
    cartReady,
    cartPersistenceError,
    markCheckoutSubmitted,
  } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const { store, loading: storeLoading } = useContext(StoreContext);
  const { showPrices, showCustomInstructions } = useDisplayPreferences();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const resumeQuoteId = searchParams.get('resumeQuoteId');

  const [quote, setQuote] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [customerSessionClientSecret, setCustomerSessionClientSecret] = useState(null);
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const [preparationError, setPreparationError] = useState(null);
  const [customInstructions, setCustomInstructions] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [contactLoading, setContactLoading] = useState(true);
  const [showContactModal, setShowContactModal] = useState(false);
  const [someoneElsePickingUp, setSomeoneElsePickingUp] = useState(false);
  const [pickupName, setPickupName] = useState('');
  const [pickupPhone, setPickupPhone] = useState('');
  const initialized = useRef(false);

  const displayCurrency = quote?.currency || store?.currency || 'USD';
  const paymentGateReady = !storeLoading &&
    paymentV2StoreGateAllows(store);
  const paymentDetailsLocked = Boolean(quote);
  const checkoutSubmissionLocked =
    getCheckoutAttempt()?.state === 'submitted';
  const isContactValid = (() => {
    const emailOk = contactEmail.trim().includes('@');
    const pickupOk = !someoneElsePickingUp || pickupName.trim().length > 0;
    const hasPhone = contactPhone.trim().length > 0 ||
      (someoneElsePickingUp && pickupPhone.trim().length > 0);
    return contactName.trim().length > 0 && emailOk && pickupOk && hasPhone;
  })();

  useEffect(() => {
    if (!cartReady || storeLoading) return;
    if (!user) {
      navigate('/cart');
      return;
    }
    const submittedQuoteId = getSubmittedQuote();
    if (submittedQuoteId && submittedQuoteId !== resumeQuoteId) {
      navigate(`/order-confirmation?quoteId=${encodeURIComponent(submittedQuoteId)}`);
      return;
    }
    const resumableQuoteId = getResumableQuote();
    if (resumableQuoteId && resumableQuoteId !== resumeQuoteId) {
      navigate(`/checkout?resumeQuoteId=${encodeURIComponent(resumableQuoteId)}`);
      return;
    }
    if (items.length === 0 && !resumeQuoteId) {
      navigate('/cart');
      return;
    }
    if (initialized.current) return;
    initialized.current = true;

    async function loadContactInfo() {
      let userName = user.displayName || '';
      let userEmail = user.email || '';
      let userPhone = user.phoneNumber || '';
      try {
        const functions = getFunctions(getApp(), 'us-central1');
        const getUserContactInfo = httpsCallable(functions, 'getUserContactInfo');
        const result = await getUserContactInfo({ platform: 'web' });
        const data = result.data;
        if (data) {
          const first = data.firstName || '';
          const last = data.lastName || '';
          userName = [first, last].filter(Boolean).join(' ') || userName;
          userEmail = data.contactEmail || userEmail;
          userPhone = data.contactPhone || userPhone;
        }
      } catch (error) {
        console.error('Failed to fetch contact info:', error);
      } finally {
        setContactName(userName);
        setContactEmail(userEmail);
        setContactPhone(userPhone);
        setContactLoading(false);
      }
    }

    async function resumeSecurePayment() {
      setIsPreparingPayment(true);
      setPreparationError(null);
      let shouldClearMarker = false;
      try {
        // Resume is a payment-creation path too. Re-read the authoritative
        // store before loading the quote or calling createPaymentIntentV2 so a
        // disabled/emergency-stopped store cannot be reopened by a stale local
        // resume marker.
        const authoritativeStore = await readAuthoritativePaymentV2Store(
          () => getDocFromServer(doc(db, 'stores', PAYMENT_V2_STORE_ID)),
        );
        if (!authoritativeStore) {
          setPreparationError(
            'Secure checkout has not been enabled for this store.',
          );
          return;
        }

        const quoteSnap = await getDoc(doc(db, 'checkout_quotes_v2', resumeQuoteId));
        if (!quoteSnap.exists()) {
          shouldClearMarker = true;
          throw new Error('The saved payment quote no longer exists.');
        }
        const quoteData = quoteSnap.data();
        if (quoteData.userId !== user.uid) {
          shouldClearMarker = true;
          throw new Error('The saved payment quote does not belong to this account.');
        }
        const immutable = quoteData.immutable || {};
        const checkoutAttemptId = quoteData.checkoutAttemptId || immutable.checkoutAttemptId;
        const recoveredQuote = parseCheckoutQuoteV2Response({
          quoteId: resumeQuoteId,
          checkoutAttemptId,
          subtotalMinor: immutable.subtotalMinor,
          taxMinor: immutable.taxMinor,
          totalMinor: immutable.totalMinor,
          amountMinor: immutable.totalMinor,
          currency: immutable.currency,
          quoteHash: quoteData.quoteHash,
          paymentCollection: immutable.paymentCollection,
          captureMethod: immutable.captureMethod,
          expiresAt: immutable.expiresAt,
          items: immutable.items,
        });
        bindCheckoutAttemptQuote(
          recoveredQuote.checkoutAttemptId,
          recoveredQuote.quoteId,
        );
        const contact = immutable.customerContact || {};
        const fulfillment = immutable.fulfillment || {};
        const pickupContact = fulfillment.pickupContact || {};
        setContactName(contact.name || '');
        setContactEmail(contact.email || '');
        setContactPhone(contact.phone || '');
        setCustomInstructions(fulfillment.instructions || '');
        setSomeoneElsePickingUp(Boolean(fulfillment.pickupContact));
        setPickupName(pickupContact.name || '');
        setPickupPhone(pickupContact.phone || '');
        setQuote(recoveredQuote);

        const functions = getFunctions(getApp(), 'us-central1');
        if (recoveredQuote.paymentCollection === 'not_required') {
          await markCheckoutSubmitted({
            userId: user.uid,
            quoteId: recoveredQuote.quoteId,
            purchasedItems: recoveredQuote.items,
          });
          if (!markQuoteSubmitted(recoveredQuote.quoteId)) {
            throw new Error('Order recovery state could not be persisted.');
          }
          await submitFreeOrderV2(functions, recoveredQuote);
          navigate(`/order-confirmation?quoteId=${encodeURIComponent(resumeQuoteId)}`);
          return;
        }
        if (stripeConfigurationError) {
          throw new Error(
            'Card payment is unavailable because this website build is missing its Stripe configuration.',
          );
        }
        const recoveredIntent = await createPaymentIntentV2(
          functions,
          recoveredQuote,
          stripeConfiguredMode,
        );
        const disposition = paymentIntentReturnDisposition(recoveredIntent.status);
        if (disposition === 'submitted') {
          await markCheckoutSubmitted({
            userId: user.uid,
            quoteId: recoveredQuote.quoteId,
            purchasedItems: recoveredQuote.items,
          });
          if (!markQuoteSubmitted(recoveredQuote.quoteId)) {
            throw new Error('Secure checkout recovery state could not be persisted.');
          }
          navigate(`/order-confirmation?quoteId=${encodeURIComponent(resumeQuoteId)}`);
          return;
        }
        if (disposition === 'restart') {
          shouldClearMarker = true;
          throw new Error('This saved payment was canceled. Start a new secure payment.');
        }
        if (disposition === 'retryable') {
          clearSubmittedQuote(resumeQuoteId);
        }
        setPaymentIntent(recoveredIntent);
        setClientSecret(recoveredIntent.clientSecret);
        setCustomerSessionClientSecret(
          recoveredIntent.customerSessionClientSecret || null,
        );
      } catch (error) {
        const reason = error?.details?.reason;
        if (
          shouldClearMarker ||
          reason === 'quote_expired' ||
          error?.code === 'functions/not-found' ||
          error?.code === 'functions/permission-denied'
        ) {
          discardCheckoutAttempt(resumeQuoteId);
        }
        logCheckoutError({
          quoteId: resumeQuoteId,
          userID: user.uid,
          step: 'payment_resume_v2',
          errorMessage: error?.message || 'Saved payment could not be resumed',
          errorCode: error?.code,
        });
        setPreparationError(checkoutErrorMessage(error));
      } finally {
        setContactLoading(false);
        setIsPreparingPayment(false);
      }
    }

    if (resumeQuoteId) {
      resumeSecurePayment();
    } else {
      loadContactInfo();
    }
  }, [
    cartReady,
    items.length,
    markCheckoutSubmitted,
    navigate,
    resumeQuoteId,
    storeLoading,
    user,
  ]);

  const handleContactModalClose = async () => {
    setShowContactModal(false);
    if (paymentDetailsLocked) return;

    try {
      const functions = getFunctions(getApp(), 'us-central1');
      const getUserContactInfo = httpsCallable(functions, 'getUserContactInfo');
      const result = await getUserContactInfo({ platform: 'web' });
      const data = result.data;
      if (data) {
        const first = data.firstName || '';
        const last = data.lastName || '';
        setContactName([first, last].filter(Boolean).join(' ') || user.displayName || '');
        setContactEmail(data.contactEmail || user.email || '');
        setContactPhone(data.contactPhone || '');
      }
    } catch (error) {
      console.error('Failed to refresh contact info:', error);
    }
  };

  const beginSecurePayment = async () => {
    if (
      isPreparingPayment ||
      clientSecret ||
      !paymentGateReady ||
      (!quote && !cartPricingValid)
    ) return;
    setIsPreparingPayment(true);
    setPreparationError(null);

    let verifiedQuote = quote;
    try {
      const functions = getFunctions(getApp(), 'us-central1');
      if (!verifiedQuote) {
        const checkoutAttempt = getOrCreateCheckoutAttempt();
        const request = buildCheckoutQuoteV2Request({
          checkoutAttemptId: checkoutAttempt.attemptId,
          storeId: store?.id,
          items,
          customerContact: {
            name: contactName,
            email: contactEmail,
            phone: contactPhone,
          },
          customInstructions,
          someoneElsePickingUp,
          pickupName,
          pickupPhone,
        });
        verifiedQuote = await createCheckoutQuoteV2(functions, request);
        bindCheckoutAttemptQuote(
          verifiedQuote.checkoutAttemptId,
          verifiedQuote.quoteId,
        );
        setQuote(verifiedQuote);
      }

      if (verifiedQuote.paymentCollection === 'not_required') {
        await markCheckoutSubmitted({
          userId: user.uid,
          quoteId: verifiedQuote.quoteId,
          purchasedItems: verifiedQuote.items,
        });
        if (!markQuoteSubmitted(verifiedQuote.quoteId)) {
          throw new Error('Order recovery state could not be persisted.');
        }
        await submitFreeOrderV2(functions, verifiedQuote);
        navigate(`/order-confirmation?quoteId=${encodeURIComponent(verifiedQuote.quoteId)}`);
        return;
      }
      if (stripeConfigurationError) {
        throw new Error(
          'Card payment is unavailable because this website build is missing its Stripe configuration.',
        );
      }
      const verifiedPaymentIntent = await createPaymentIntentV2(
        functions,
        verifiedQuote,
        stripeConfiguredMode,
      );
      const disposition = paymentIntentReturnDisposition(verifiedPaymentIntent.status);
      if (disposition === 'submitted') {
        await markCheckoutSubmitted({
          userId: user.uid,
          quoteId: verifiedQuote.quoteId,
          purchasedItems: verifiedQuote.items,
        });
        if (!markQuoteSubmitted(verifiedQuote.quoteId)) {
          throw new Error('Secure checkout recovery state could not be persisted.');
        }
        navigate(`/order-confirmation?quoteId=${encodeURIComponent(verifiedQuote.quoteId)}`);
        return;
      }
      if (disposition === 'restart') {
        discardCheckoutAttempt(verifiedQuote.quoteId);
        throw new Error('This saved payment was canceled. Start a new secure payment.');
      }
      setPaymentIntent(verifiedPaymentIntent);
      setClientSecret(verifiedPaymentIntent.clientSecret);
      setCustomerSessionClientSecret(
        verifiedPaymentIntent.customerSessionClientSecret || null,
      );
    } catch (error) {
      const conflictQuoteId = error?.details?.quoteId;
      const serverAttemptId = error?.details?.checkoutAttemptId;
      const activeAttempt = getCheckoutAttempt();
      if (
        error?.details?.reason === 'active_checkout_exists' &&
        typeof conflictQuoteId === 'string' &&
        typeof serverAttemptId === 'string'
      ) {
        try {
          adoptServerCheckoutAttempt({
            active: true,
            quoteId: conflictQuoteId,
            checkoutAttemptId: serverAttemptId,
            state: error?.details?.state || 'quoted',
          });
          navigate(`/checkout?resumeQuoteId=${encodeURIComponent(conflictQuoteId)}`);
          return;
        } catch {
          // Continue to the fail-closed, user-visible preparation error.
        }
      }
      if (
        error?.details?.reason === 'checkout_attempt_conflict' &&
        typeof conflictQuoteId === 'string' &&
        activeAttempt?.attemptId
      ) {
        try {
          bindCheckoutAttemptQuote(activeAttempt.attemptId, conflictQuoteId);
          navigate(`/checkout?resumeQuoteId=${encodeURIComponent(conflictQuoteId)}`);
          return;
        } catch {
          // Continue to the fail-closed, user-visible preparation error.
        }
      }
      logCheckoutError({
        quoteId: verifiedQuote?.quoteId,
        userID: user?.uid,
        step: verifiedQuote ? 'payment_intent_creation_v2' : 'quote_creation_v2',
        errorMessage: error?.message || 'Secure checkout preparation failed',
        errorCode: error?.code,
      });
      setPreparationError(checkoutErrorMessage(error));
    } finally {
      setIsPreparingPayment(false);
    }
  };

  const resetUnconfirmedQuote = () => {
    if (clientSecret || checkoutSubmissionLocked) return;
    discardCheckoutAttempt(quote?.quoteId);
    setQuote(null);
    setPaymentIntent(null);
    setCustomerSessionClientSecret(null);
    setPreparationError(null);
  };

  if (!cartPersistenceError && (contactLoading || storeLoading || !cartReady)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100dvh - 120px)' }}>
        <div style={{
          width: 32,
          height: 32,
          border: '3px solid #e0e0e0',
          borderTop: '3px solid #666',
          borderRadius: '50%',
          animation: 'spin 0.8s linear infinite',
        }} />
      </div>
    );
  }

  if (cartPersistenceError) {
    return (
      <div style={{ maxWidth: MAX_CONTENT_WIDTH, margin: '32px auto', textAlign: 'center' }}>
        <p style={{ color: '#df1b41' }}>
          Your cart could not be safely loaded, so checkout is unavailable. Reload this page to retry.
        </p>
        <button onClick={() => navigate('/cart')} style={{ marginTop: 10, padding: '6px 20px', cursor: 'pointer' }}>
          Back to Cart
        </button>
      </div>
    );
  }

  if (!store?.id) {
    return (
      <div style={{ maxWidth: MAX_CONTENT_WIDTH, margin: '32px auto', textAlign: 'center' }}>
        <p style={{ color: '#df1b41' }}>The store could not be loaded, so checkout is unavailable.</p>
        <button onClick={() => navigate('/cart')} style={{ marginTop: 10, padding: '6px 20px', cursor: 'pointer' }}>
          Back to Cart
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 480, margin: '32px auto', padding: '0 12px' }}>
      <h2 style={{ marginBottom: 20 }}>Check Out</h2>

      {showPrices && (
        <div style={{ marginBottom: 20, padding: 12, backgroundColor: 'var(--secondary-bg, #f5f5f5)', borderRadius: 6 }}>
          {!quote && (
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary, #666)', marginBottom: 8 }}>
              Estimated until verified by the store
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span>Bag Subtotal</span>
            <span>{formatPrice(quote?.subtotalMinor ?? cartSubtotal, displayCurrency)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span>Tax</span>
            <span>{formatPrice(quote?.taxMinor ?? cartTax, displayCurrency)}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 600, borderTop: '1px solid #ddd', paddingTop: 6 }}>
            <span>{quote ? 'Verified Total' : 'Estimated Total'}</span>
            <span>{formatPrice(quote?.totalMinor ?? estimatedTotal, displayCurrency)}</span>
          </div>
        </div>
      )}

      <div style={{
        marginBottom: 20,
        border: '1px solid var(--color-border, #e5e7eb)',
        borderRadius: 6,
        overflow: 'hidden',
      }}>
        <div style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--color-text-secondary, #666)',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          padding: '10px 12px 6px',
        }}>
          Contact Info
        </div>
        {[
          ['Name', contactName || 'Required'],
          ['Email', contactEmail || 'Required'],
          ['Phone', contactPhone || '—'],
        ].map(([label, value]) => (
          <button
            key={label}
            type="button"
            onClick={() => setShowContactModal(true)}
            disabled={paymentDetailsLocked}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              padding: '10px 12px',
              background: 'none',
              border: 'none',
              cursor: paymentDetailsLocked ? 'default' : 'pointer',
              fontSize: 14,
              textAlign: 'left',
              borderTop: '1px solid var(--color-border, #e5e7eb)',
            }}
          >
            <span style={{ color: 'var(--color-text-primary, #111)' }}>{label}</span>
            <span style={{
              color: value === 'Required' ? '#df1b41' : 'var(--color-text-secondary, #666)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '60%',
              textAlign: 'right',
            }}>
              {value}
            </span>
          </button>
        ))}
      </div>

      {showContactModal && !paymentDetailsLocked && (
        <ContactInfoModal onClose={handleContactModalClose} />
      )}

      {showCustomInstructions && (
        <div style={{ marginBottom: 20 }}>
          <textarea
            placeholder="Special instructions (optional)"
            value={customInstructions}
            disabled={paymentDetailsLocked}
            onChange={event => setCustomInstructions(event.target.value)}
            rows={3}
            style={{
              width: '100%',
              padding: '8px 10px',
              fontSize: 14,
              fontFamily: 'inherit',
              border: '1px solid var(--color-border, #d1d5db)',
              borderRadius: 6,
              backgroundColor: 'var(--color-background, #fff)',
              color: 'var(--color-text-primary, #111)',
              resize: 'vertical',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
      )}

      <div style={{ marginBottom: 20 }}>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: paymentDetailsLocked ? 'default' : 'pointer',
          padding: '8px 0',
        }}>
          <span style={{ fontSize: 14, color: 'var(--color-text-primary, #111)' }}>
            Someone else is picking up
          </span>
          <input
            type="checkbox"
            checked={someoneElsePickingUp}
            disabled={paymentDetailsLocked}
            onChange={event => setSomeoneElsePickingUp(event.target.checked)}
            style={{ accentColor: 'var(--accent-color, #000)' }}
          />
        </label>
        {someoneElsePickingUp && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingTop: 8 }}>
            <input
              type="text"
              placeholder="Pickup person's name"
              value={pickupName}
              disabled={paymentDetailsLocked}
              onChange={event => setPickupName(event.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                fontSize: 14,
                fontFamily: 'inherit',
                border: '1px solid var(--color-border, #d1d5db)',
                borderRadius: 6,
                backgroundColor: 'var(--color-background, #fff)',
                color: 'var(--color-text-primary, #111)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <input
              type="tel"
              placeholder="Pickup person's phone"
              value={pickupPhone}
              disabled={paymentDetailsLocked}
              onChange={event => setPickupPhone(event.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                fontSize: 14,
                fontFamily: 'inherit',
                border: '1px solid var(--color-border, #d1d5db)',
                borderRadius: 6,
                backgroundColor: 'var(--color-background, #fff)',
                color: 'var(--color-text-primary, #111)',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <p style={{ fontSize: 12, color: 'var(--color-text-secondary, #666)', marginTop: 0 }}>
              The pickup person may need to show ID.
            </p>
          </div>
        )}
      </div>

      {quote && (
        <div style={{ fontSize: 13, color: 'var(--color-text-secondary, #666)', marginBottom: 12 }}>
          Your contact, pickup details, items, currency, and total are locked to secure quote {quote.quoteId}.
        </div>
      )}

      {preparationError && (
        <div style={{ color: '#df1b41', fontSize: 14, marginBottom: 12 }}>
          {preparationError}
        </div>
      )}

      {stripeConfigurationError && quote?.paymentCollection === 'stripe' && (
        <div style={{ color: '#df1b41', fontSize: 14, marginBottom: 12 }}>
          Card payment is currently unavailable because Stripe is not configured.
        </div>
      )}

      {!paymentGateReady && (
        <div style={{ color: '#df1b41', fontSize: 14, marginBottom: 12 }}>
          Secure checkout has not been enabled for this store.
        </div>
      )}

      {!quote && !cartPricingValid && (
        <div style={{ color: '#df1b41', fontSize: 14, marginBottom: 12 }}>
          The catalog does not have a verified integer minor-unit price schema.
        </div>
      )}

      {!clientSecret && (
        <button
          type="button"
          onClick={beginSecurePayment}
          disabled={
            isPreparingPayment ||
            !isContactValid ||
            !store?.id ||
            !paymentGateReady ||
            (!quote && !cartPricingValid)
          }
          style={{
            width: '100%',
            padding: '10px 0',
            backgroundColor: (
              isPreparingPayment ||
              !isContactValid ||
              !paymentGateReady ||
              (!quote && !cartPricingValid)
            ) ? '#999' : 'var(--accent-color, #000)',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 16,
            fontWeight: 600,
            cursor: isPreparingPayment ? 'wait' : 'pointer',
          }}
        >
          {isPreparingPayment
            ? 'Verifying your order...'
            : quote
              ? quote.paymentCollection === 'not_required'
                ? 'Retry order'
                : 'Retry secure payment'
              : 'Review and place order'}
        </button>
      )}

      {quote && !clientSecret && !checkoutSubmissionLocked && (
        <button
          type="button"
          onClick={resetUnconfirmedQuote}
          disabled={isPreparingPayment}
          style={{ width: '100%', marginTop: 10, padding: 8, cursor: 'pointer' }}
        >
          Edit checkout details
        </button>
      )}

      {clientSecret &&
        quote?.paymentCollection === 'stripe' &&
        paymentIntent &&
        paymentGateReady &&
        !stripeConfigurationError && (
        <Elements
          key={quote.quoteId}
          stripe={stripePromise}
          options={{
            clientSecret,
            customerSessionClientSecret: customerSessionClientSecret || undefined,
            appearance: { theme: 'stripe' },
          }}
        >
          <CheckoutForm quote={quote} clientSecret={clientSecret} />
        </Elements>
      )}

      {!isContactValid && (
        <div style={{ fontSize: 13, color: '#df1b41', textAlign: 'center', marginTop: 12 }}>
          {!contactName.trim() ? 'Name is required to check out.' :
           !contactEmail.trim().includes('@') ? 'A valid email is required to check out.' :
           !contactPhone.trim() && !(someoneElsePickingUp && pickupPhone.trim()) ? 'Phone number is required to check out.' :
           'Please complete required fields to check out.'}
        </div>
      )}
    </div>
  );
}
